import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/client";
import type { TestRow } from "../api/types";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/ui";

interface TestsResponse {
  tests: TestRow[];
}

export function TestsPage() {
  const { data, loading, error, refetch } = useFetch<TestsResponse>(
    "/admin/tests",
  );
  const [actionError, setActionError] = useState<Error | null>(null);

  async function createTest(title: string) {
    setActionError(null);
    try {
      await apiFetch("/admin/tests", {
        method: "POST",
        body: JSON.stringify({ title }),
      });
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  async function deleteTest(id: string) {
    if (!confirm("Delete this test? This cannot be undone.")) return;
    try {
      await apiFetch(`/admin/tests/${id}`, { method: "DELETE" });
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  return (
    <section>
      <PageHeader
        title="Test management"
        subtitle="Create tests, edit questions, assign students, and grade submissions."
      />
      <ErrorBanner error={error ?? actionError} />
      <CreateTestForm onSubmit={createTest} />
      <h2>Tests</h2>
      {loading ? (
        <LoadingState />
      ) : data && data.tests.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Starts</th>
              <th>Ends</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.tests.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/admin/tests/${t.id}`}>{t.title}</Link>
                </td>
                <td>
                  <StatusBadge status={t.status} />
                </td>
                <td>{t.starts_at ?? "—"}</td>
                <td>{t.ends_at ?? "—"}</td>
                <td>
                  <Link to={`/admin/tests/${t.id}`}>Edit</Link>{" · "}
                  <Link to={`/admin/tests/${t.id}/assignments`}>
                    Assignments
                  </Link>{" · "}
                  <Link to={`/admin/tests/${t.id}/submissions`}>
                    Submissions
                  </Link>{" "}
                  <button
                    className="btn-danger"
                    onClick={() => void deleteTest(t.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          title="No tests yet"
          hint="Create your first test with the form above."
        />
      )}
    </section>
  );
}

function CreateTestForm({ onSubmit }: { onSubmit: (title: string) => void }) {
  const [title, setTitle] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit(title.trim());
    setTitle("");
  }
  return (
    <form className="inline-form admin-form" onSubmit={submit}>
      <fieldset>
        <legend>Create test</legend>
        <label className="field-block">
          Title
          <input
            className="field-wide"
            placeholder="e.g. Algebra Final"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-primary">
          Create
        </button>
      </fieldset>
    </form>
  );
}
